import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MessageSquarePlus, Inbox, ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import API from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, formatDate } from "@/lib/qa";

export default function UserDashboard() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const firstName = user.full_name.split(" ")[0];

    useEffect(() => { API.get("/questions/me").then(({ data }) => setItems(data)).catch(() => {}); }, []);

    const total = items.length;
    const waiting = items.filter(q => q.status === "received" || q.status === "in_progress").length;
    const answered = items.filter(q => q.status === "answered" || q.status === "published").length;

    // Each card links to the same /dashboard/richieste but with a query param
    // that the list page reads to pre-select the matching filter bucket.
    // No DB / demo data changes involved.
    const kpis = [
        { label: "Richieste totali", value: total, to: "/dashboard/richieste?stato=tutte", testid: "kpi-total" },
        { label: "In attesa di risposta", value: waiting, to: "/dashboard/richieste?stato=attesa", testid: "kpi-waiting" },
        { label: "Risposte ricevute", value: answered, to: "/dashboard/richieste?stato=risposte", testid: "kpi-answered" },
    ];

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
            <div className="mb-8">
                <div className="text-xs uppercase tracking-widest text-primary font-semibold">Area riservata</div>
                <h1 data-testid="dashboard-title" className="mt-1 font-serif text-3xl lg:text-4xl font-semibold">Ciao, {firstName}</h1>
                <p className="mt-2 text-muted-foreground">Panoramica delle tue richieste.</p>
            </div>

            <div className="grid gap-4 sm:grid-cols-3 mb-6">
                {kpis.map(k => (
                    <Link
                        key={k.label}
                        to={k.to}
                        data-testid={k.testid}
                        aria-label={`${k.label}: ${k.value}. Apri elenco filtrato`}
                        className="group rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    >
                        <Card className="border-slate-200 h-full transition group-hover:border-primary/40 group-hover:shadow-md">
                            <CardContent className="pt-6">
                                <div className="text-4xl font-serif font-semibold text-primary">{k.value}</div>
                                <div className="mt-1 flex items-center gap-1 text-sm text-muted-foreground group-hover:text-primary transition">
                                    {k.label}
                                    <ArrowRight className="size-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition" />
                                </div>
                            </CardContent>
                        </Card>
                    </Link>
                ))}
            </div>

            <Card className="border-slate-200 mb-6">
                <CardContent className="py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div>
                        <div className="font-semibold text-slate-900">Hai un dubbio sul condominio?</div>
                        <div className="text-sm text-muted-foreground">Invia una nuova domanda: risponde un professionista reale.</div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <Button asChild variant="outline" data-testid="dash-my"><Link to="/dashboard/richieste"><Inbox className="size-4 mr-2" />Le mie richieste</Link></Button>
                        <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="dash-new"><Link to="/dashboard/nuova"><MessageSquarePlus className="size-4 mr-2" />Nuova domanda</Link></Button>
                    </div>
                </CardContent>
            </Card>

            {items.length > 0 && (
                <div className="space-y-3" data-testid="dash-recent">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold px-1">Ultime richieste</div>
                    {items.slice(0, 5).map(q => (
                        <Link key={q.id} to={`/dashboard/richieste/${q.id}`} className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-primary/30 transition">
                            <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 mb-1"><StatusBadge status={q.status} />{q.category && <span className="text-xs text-muted-foreground">Domanda</span>}</div>
                                    <div className="font-semibold text-slate-900 truncate">{q.title}</div>
                                    <div className="text-xs text-muted-foreground">{formatDate(q.created_at)}</div>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
